import React, { useMemo, useState } from 'react'
import { CAlert, CBadge, CButton, CCard, CCardBody, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload, cilWarning } from '@coreui/icons'
import moment from 'moment'
import Bootbox from 'bootbox-react'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import TotrTeamForm from './TotrTeamForm'
import TotrPointsForm from './TotrPointsForm'
import { isSetUp } from './totrUtils'

const SEARCH_FIELDS = [{ key: 'round', label: 'Round' }]

const columns = [
  { key: 'round', label: 'Round', filter: false, _style: { width: '12%' } },
  { key: 'duration', label: 'Duration', filter: false, sorter: false, _style: { width: '12%' } },
  { key: 'status', label: 'Status', filter: false, _style: { width: '18%' } },
  { key: 'actions', label: '', filter: false, sorter: false, _style: { width: '12%' } },
]

const TeamOfTheWeekTable = ({ rounds, loading, error, fetchData }) => {
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [teamRound, setTeamRound] = useState(null)
  const [pointsRound, setPointsRound] = useState(null)
  const [removeRound, setRemoveRound] = useState(null)

  // The endpoint returns every round in one call, so search and paging happen here.
  const items = useMemo(() => {
    const term = searchValue.trim().toLowerCase()
    if (!term) return rounds
    return rounds.filter((row) =>
      String(row[searchField] ?? '')
        .toLowerCase()
        .includes(term),
    )
  }, [rounds, searchField, searchValue])

  const removeTeam = () => {
    const round = removeRound
    setRemoveRound(null)
    CommonService.revertTotw({ totwId: round.totw_id })
      .then((res) => {
        if (res.success) {
          ToastComponent(res.message, 'success')
          fetchData()
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch(() => ToastComponent('Could not remove the team', 'error'))
  }

  const noItemsLabel = searchValue.trim()
    ? 'No rounds match your search'
    : 'No rounds yet - add rounds before picking a Team of the Round'

  return (
    <CCard className="totr-panel">
      <CCardBody>
        <div className="totr-panel-heading">
          <h2 className="totr-panel-title">Round Teams</h2>
          <p className="totr-panel-note">
            One team per round. Pick its players, then award them points once the round is played.
          </p>
        </div>

        {error ? (
          <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2 mb-0">
            <CIcon icon={cilWarning} className="flex-shrink-0" />
            <span>We couldn&apos;t load the rounds.</span>
            <CButton
              color="danger"
              variant="outline"
              size="sm"
              className="ms-auto"
              onClick={fetchData}
            >
              <CIcon icon={cilReload} className="me-1" />
              Retry
            </CButton>
          </CAlert>
        ) : (
          <>
            <TableSearchToolbar
              fields={SEARCH_FIELDS}
              field={searchField}
              onFieldChange={(field) => {
                setSearchField(field)
                setSearchValue('')
              }}
              value={searchValue}
              onSearch={setSearchValue}
              placeholder="Search rounds..."
            />

            <div className="ge-datatable ge-datatable--no-select">
              <CSmartTable
                columns={columns}
                items={items}
                itemsPerPage={itemsPerPage}
                itemsPerPageSelect
                loading={loading}
                noItemsLabel={noItemsLabel}
                pagination
                columnSorter
                scopedColumns={{
                  round: (item) => (
                    <td>
                      <span className="totr-round">R{item.round}</span>
                    </td>
                  ),
                  duration: (item) => (
                    <td className="totr-muted">
                      {moment(item.start_date).format('MMM D')} -{' '}
                      {moment(item.end_date).format('MMM D')}
                    </td>
                  ),
                  status: (item) => (
                    <td>
                      <CBadge shape="rounded-pill" color={isSetUp(item) ? 'success' : 'secondary'}>
                        {isSetUp(item) ? 'Team picked' : 'Not set up'}
                      </CBadge>
                    </td>
                  ),
                  actions: (item) => (
                    <td className="text-center">
                      <RowActionsMenu
                        actions={[
                          {
                            label: isSetUp(item) ? 'Edit Team' : 'Pick Team',
                            onClick: () => setTeamRound(item),
                          },
                          {
                            label: 'Player Points',
                            hidden: !isSetUp(item),
                            onClick: () => setPointsRound(item),
                          },
                          {
                            label: 'Remove Team',
                            hidden: !isSetUp(item),
                            danger: true,
                            onClick: () => setRemoveRound(item),
                          },
                        ]}
                      />
                    </td>
                  ),
                }}
                tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          </>
        )}
      </CCardBody>

      <TotrTeamForm round={teamRound} onClose={() => setTeamRound(null)} onSaved={fetchData} />
      <TotrPointsForm
        round={pointsRound}
        onClose={() => setPointsRound(null)}
        onSaved={fetchData}
      />
      <Bootbox
        show={!!removeRound}
        type={'confirm'}
        message={`Remove the Round ${removeRound?.round} Team of the Round? Its players and any points awarded to them will be deleted.`}
        onSuccess={removeTeam}
        onCancel={() => setRemoveRound(null)}
        onClose={() => setRemoveRound(null)}
      />
    </CCard>
  )
}

export default TeamOfTheWeekTable
